import type { ComponentPropsWithoutRef, SyntheticEvent } from "react"
import { useRef } from "react"
import { t } from "@lingui/core/macro"
import { useAppContext } from "../../../hooks/appContext"
import {
    useDeleteReactionMutation,
    useGetProfileQuery,
} from "../../../store/messagingSlice"
import { addPageAction } from "../../../utils/newrelic"
import { BlackClose } from "../../common/atoms/Icons/Chat"
import { ProfilePicture } from "../common/ProfilePicture/ProfilePicture"
import { getReactionName } from "../ReactionPicker/ReactionPicker"
import { EmojiData, ReactionEmoji } from "../types"
import styles from "./Reactions.module.scss"
import type { Message } from "../types"
import type { IUserInfo } from "@multimediallc/web-utils/types"

interface ReactionsProps extends ComponentPropsWithoutRef<"div"> {
    reactionMessage: Message
    onClose: () => void
    otherUser: IUserInfo | undefined
    thisUsername: string | undefined
}

export function Reactions({
    reactionMessage,
    onClose,
    otherUser,
    thisUsername,
}: ReactionsProps) {
    const reactionDivs = []
    const ownReactionDiv = useRef<HTMLDivElement | null>(null)
    const [deleteReaction] = useDeleteReactionMutation()
    const { context: appContext } = useAppContext()
    const username = appContext.logged_in_user?.username
    const { data: thisUser } = useGetProfileQuery(thisUsername || "")
    if (
        reactionMessage.reactions !== undefined &&
        (reactionMessage.reactions !== undefined) !== null
    ) {
        for (const [key, value] of Object.entries(reactionMessage.reactions)) {
            const ownReaction = key === username
            const selection = value.split(":")[0]
            let onClick = (e: React.MouseEvent) => {}
            if (ownReaction) {
                onClick = (e: React.MouseEvent) => {
                    e.preventDefault()
                    if (ownReactionDiv.current !== null) {
                        ownReactionDiv.current.style.backgroundColor = "#EFEFEF"
                        const reactionEmoji = EmojiData.get(selection)
                        deleteReaction({ message_id: reactionMessage.i })
                            .then(() => {
                                addPageAction("DmUpdateReaction", {
                                    reactionAction: "delete",
                                    reactionId: getReactionName(
                                        reactionEmoji ?? ReactionEmoji.none,
                                    ),
                                })
                                ownReactionDiv.current?.remove()
                            })
                            .catch(() => {})
                    }
                }
            }
            const ref = ownReaction ? ownReactionDiv : undefined
            const emoji = EmojiData.get(selection)
            let user: IUserInfo = {
                username: key,
                isBroadcaster: false,
                inFanclub: false,
                isFollowing: false,
                hasTokens: false,
                isMod: false,
                tippedRecently: false,
                tippedALotRecently: false,
                tippedTonsRecently: false,
                gender: "",
            }
            if (otherUser !== undefined && key === otherUser.username) {
                user = otherUser
            } else if (thisUser !== undefined && key === thisUser?.username) {
                user = thisUser
            }
            const reactionDiv = (
                <div
                    className={styles.reactionsContent}
                    onClick={onClick}
                    ref={ref}
                >
                    <ProfilePicture user={user} size={24} />
                    <div className={styles.reactionsUsername}>
                        <div>{key}</div>
                        {ownReaction && (
                            <div
                                className={styles.reactionsRemove}
                            >{t`Tap to remove`}</div>
                        )}
                    </div>
                    <div className={styles.reactionsReaction}>{emoji}</div>
                </div>
            )
            if (ownReaction) {
                reactionDivs.unshift(reactionDiv)
            } else {
                reactionDivs.push(reactionDiv)
            }
        }
    }

    const absorb = (e: SyntheticEvent) => {
        e.stopPropagation()
    }

    return (
        <div className={styles.reactionsBackdrop} onClick={onClose}>
            <div className={styles.reactionsOuterOverlay} onClick={absorb}>
                <button
                    className={styles.reactionsCloseX}
                    onClick={onClose}
                    data-testid="reactions-close-button"
                >
                    <BlackClose height={24} width={24} />
                </button>
                <div
                    className={styles.reactionsContentHeader}
                    data-testid="reactions-header"
                >
                    {t`Reactions`}
                </div>
                <div className={styles.reactionsInnerOverlay}>
                    {reactionDivs}
                </div>
            </div>
        </div>
    )
}
